import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const links = [
  { id: "work", label: "Work" },
  { id: "about", label: "Essence" },
  { id: "services", label: "Services" },
  { id: "process", label: "Process" },
  { id: "brands", label: "Clients" },
  { id: "contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.nav
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.6, 0.05, 0.05, 0.99] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#0F211A]/80 border-b border-[#264738]/60" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3"
          data-testid="nav-logo"
        >
          <img src={LOGO_URL} alt="StyleePix Collective" className="h-12 w-12 object-contain" />
          <div className="hidden md:flex flex-col leading-none">
            <span className="font-display text-[15px] tracking-[0.3em] text-ivory">STYLEEPIX</span>
            <span className="text-[9px] tracking-[0.45em] text-gold mt-0.5">COLLECTIVE</span>
          </div>
        </button>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="gold-link text-[11px] uppercase tracking-[0.3em]"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => scrollTo("contact")}
          className="hidden md:inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.3em] uppercase border border-gold text-gold hover:bg-gold hover:text-forest transition-colors duration-300"
          data-testid="nav-cta"
        >
          Start a Project
        </button>

        <button
          className="md:hidden text-ivory"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0F211A]/95 border-t border-[#264738]/60 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="text-left text-sm uppercase tracking-[0.3em] text-ivory"
                  data-testid={`nav-mobile-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo("contact")}
                className="self-start mt-3 px-5 py-3 text-[11px] tracking-[0.3em] uppercase border border-gold text-gold"
              >
                Start a Project
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
